import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { tasksAPI } from '../services/api';
import Sidebar from '../components/Sidebar';
import ConfirmModal from '../components/ConfirmModal';
import Toast from '../components/Toast';
import './Tasks.css';

const PRIORITY_CONFIG = {
  urgent: { label: 'URGENT', color: '#DC2626', bg: '#FEE2E2' },
  high: { label: 'HIGH', color: '#EA580C', bg: '#FFEDD5' },
  medium: { label: 'MEDIUM', color: '#2563EB', bg: '#DBEAFE' },
  low: { label: 'LOW', color: '#6B7280', bg: '#F3F4F6' }
};

const STATUS_CONFIG = {
  todo: { label: 'TO DO', color: '#6B7280', bg: '#F3F4F6' },
  in_progress: { label: 'IN PROGRESS', color: '#D97706', bg: '#FEF3C7' },
  done: { label: 'DONE', color: '#16A34A', bg: '#DCFCE7' }
};

const Tasks = () => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, todo: 0, in_progress: 0, done: 0 });
  const [filters, setFilters] = useState({ status: '', priority: '', search: '', page: 1, limit: 50 });
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1 });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [toast, setToast] = useState(null);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [formData, setFormData] = useState({ title: '', description: '', priority: 'medium', assigned_to: '', due_date: '' });
  const statusDropdownRefs = useRef({});

  const fetchTasks = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await tasksAPI.getAll(filters);
      setTasks(data.data.tasks);
      setPagination(data.data.pagination);
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await tasksAPI.getStats();
      setStats(data.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, []);

  useEffect(() => {
    fetchTasks();
    fetchStats();
  }, [fetchTasks, fetchStats]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (statusDropdownOpen) {
        const ref = statusDropdownRefs.current[statusDropdownOpen];
        const portalMenu = document.querySelector('.task-status-dropdown-menu');
        if (ref && !ref.contains(event.target) && (!portalMenu || !portalMenu.contains(event.target))) {
          setStatusDropdownOpen(null);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [statusDropdownOpen]);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleCreate = async () => {
    if (!formData.title.trim()) return;
    try {
      await tasksAPI.create(formData);
      setShowCreateModal(false);
      setFormData({ title: '', description: '', priority: 'medium', assigned_to: '', due_date: '' });
      fetchTasks();
      fetchStats();
      showToast('Task created');
    } catch (error) {
      showToast('Failed to create task', 'error');
    }
  };

  const handleUpdate = async () => {
    if (!formData.title.trim()) return;
    try {
      await tasksAPI.update(editingTask.id, formData);
      setEditingTask(null);
      setFormData({ title: '', description: '', priority: 'medium', assigned_to: '', due_date: '' });
      fetchTasks();
      fetchStats();
      showToast('Task updated');
    } catch (error) {
      showToast('Failed to update task', 'error');
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await tasksAPI.updateStatus(id, newStatus);
      fetchTasks();
      fetchStats();
      setStatusDropdownOpen(null);
    } catch (error) {
      showToast('Failed to update status', 'error');
    }
  };

  const handleDelete = async (id) => {
    try {
      await tasksAPI.delete(id);
      setDeleteConfirm(null);
      fetchTasks();
      fetchStats();
      showToast('Task deleted');
    } catch (error) {
      showToast('Failed to delete task', 'error');
    }
  };

  const openEdit = (task) => {
    setFormData({
      title: task.title,
      description: task.description || '',
      priority: task.priority,
      assigned_to: task.assigned_to || '',
      due_date: task.due_date ? task.due_date.split('T')[0] : ''
    });
    setEditingTask(task);
  };

  const handleSearch = (e) => {
    setFilters(prev => ({ ...prev, search: e.target.value, page: 1 }));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const isOverdue = (task) => {
    if (!task.due_date || task.status === 'done') return false;
    return new Date(task.due_date) < new Date();
  };

  const statuses = Object.entries(STATUS_CONFIG);

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <main className="dashboard-main">
        <div className="dashboard-header">
          <div className="header-left">
            <h1 className="page-title">TASKS</h1>
            <p className="page-subtitle">PLATFORM CHANGES TRACKER</p>
          </div>
          <div className="header-right">
            <div className="search-box">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.35-4.35"></path>
              </svg>
              <input
                type="text"
                placeholder="Search tasks..."
                value={filters.search}
                onChange={handleSearch}
              />
            </div>
            <button className="btn-primary" onClick={() => {
              setFormData({ title: '', description: '', priority: 'medium', assigned_to: '', due_date: '' });
              setShowCreateModal(true);
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 5v14M5 12h14"></path>
              </svg>
              NEW TASK
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="stats-grid tasks-stats">
          <div className="stat-card">
            <div className="stat-value">{stats.total || 0}</div>
            <div className="stat-label">TOTAL</div>
          </div>
          <div className="stat-card stat-todo">
            <div className="stat-value">{stats.todo || 0}</div>
            <div className="stat-label">TO DO</div>
          </div>
          <div className="stat-card stat-in-progress">
            <div className="stat-value">{stats.in_progress || 0}</div>
            <div className="stat-label">IN PROGRESS</div>
          </div>
          <div className="stat-card stat-done">
            <div className="stat-value">{stats.done || 0}</div>
            <div className="stat-label">DONE</div>
          </div>
        </div>

        {/* Filter pills */}
        <div className="filter-pills">
          <button
            className={`filter-pill ${!filters.status ? 'active' : ''}`}
            onClick={() => setFilters(prev => ({ ...prev, status: '', page: 1 }))}
          >ALL</button>
          {statuses.map(([key, config]) => (
            <button
              key={key}
              className={`filter-pill ${filters.status === key ? 'active' : ''}`}
              onClick={() => setFilters(prev => ({ ...prev, status: key, page: 1 }))}
              style={filters.status === key ? { background: config.bg, color: config.color, borderColor: config.color } : {}}
            >{config.label}</button>
          ))}
        </div>

        {/* Tasks list */}
        <div className="tasks-section card">
          {loading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading tasks...</p>
            </div>
          ) : tasks.length === 0 ? (
            <div className="empty-state">
              <p>No tasks found</p>
            </div>
          ) : (
            <div className="tasks-list">
              {tasks.map((task) => (
                <div key={task.id} className={`task-row ${task.status === 'done' ? 'task-done' : ''} ${isOverdue(task) ? 'task-overdue' : ''}`}>
                  <div className="task-check">
                    <button
                      className={`check-btn ${task.status === 'done' ? 'checked' : ''}`}
                      onClick={() => handleStatusChange(task.id, task.status === 'done' ? 'todo' : 'done')}
                    >
                      {task.status === 'done' && (
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                          <path d="M20 6L9 17l-5-5"></path>
                        </svg>
                      )}
                    </button>
                  </div>
                  <div className="task-content">
                    <div className="task-title-row">
                      <span className="task-title">{task.title}</span>
                      <div className="task-badges">
                        <span className="priority-badge" style={{ background: PRIORITY_CONFIG[task.priority].bg, color: PRIORITY_CONFIG[task.priority].color }}>
                          {PRIORITY_CONFIG[task.priority].label}
                        </span>
                        <div
                          className="status-custom-dropdown"
                          ref={el => statusDropdownRefs.current[task.id] = el}
                        >
                          <button
                            className="status-badge-btn"
                            style={{ background: STATUS_CONFIG[task.status].bg, color: STATUS_CONFIG[task.status].color }}
                            onClick={(e) => {
                              if (statusDropdownOpen === task.id) {
                                setStatusDropdownOpen(null);
                              } else {
                                const rect = e.currentTarget.getBoundingClientRect();
                                const spaceBelow = window.innerHeight - rect.bottom;
                                const menuHeight = statuses.length * 36 + 8;
                                const openUpward = spaceBelow < menuHeight;
                                setDropdownPosition({
                                  top: openUpward ? rect.top - menuHeight : rect.bottom + 4,
                                  left: rect.left
                                });
                                setStatusDropdownOpen(task.id);
                              }
                            }}
                          >
                            {STATUS_CONFIG[task.status].label}
                            <svg width="8" height="8" viewBox="0 0 12 12" fill="none">
                              <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </button>
                          {statusDropdownOpen === task.id && ReactDOM.createPortal(
                            <div className="task-status-dropdown-menu" style={{ top: dropdownPosition.top, left: dropdownPosition.left }}>
                              {statuses.map(([key, config]) => (
                                <div
                                  key={key}
                                  className={`task-status-dropdown-item ${task.status === key ? 'active' : ''}`}
                                  onClick={() => handleStatusChange(task.id, key)}
                                >
                                  <span className="status-dot" style={{ background: config.color }}></span>
                                  {config.label}
                                </div>
                              ))}
                            </div>,
                            document.body
                          )}
                        </div>
                      </div>
                    </div>
                    {task.description && <p className="task-description">{task.description}</p>}
                    <div className="task-meta">
                      {task.assigned_to && (
                        <span className="task-meta-item">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"></path>
                            <circle cx="12" cy="7" r="4"></circle>
                          </svg>
                          {task.assigned_to}
                        </span>
                      )}
                      {task.due_date && (
                        <span className={`task-meta-item ${isOverdue(task) ? 'overdue' : ''}`}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                            <line x1="16" y1="2" x2="16" y2="6"></line>
                            <line x1="8" y1="2" x2="8" y2="6"></line>
                            <line x1="3" y1="10" x2="21" y2="10"></line>
                          </svg>
                          {formatDate(task.due_date)}
                        </span>
                      )}
                      {task.created_by_name && (
                        <span className="task-meta-item task-meta-creator">by {task.created_by_name}</span>
                      )}
                    </div>
                  </div>
                  <div className="task-actions">
                    <button className="btn-icon" onClick={() => openEdit(task)} title="Edit">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                      </svg>
                    </button>
                    <button className="btn-icon btn-icon-danger" onClick={() => setDeleteConfirm(task)} title="Delete">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="3,6 5,6 21,6"></polyline>
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="table-footer">
              <span className="pagination-info">
                Page {filters.page} of {pagination.totalPages} ({pagination.total} tasks)
              </span>
              <div className="pagination-controls">
                <button
                  className="btn-page"
                  disabled={filters.page <= 1}
                  onClick={() => setFilters(prev => ({ ...prev, page: prev.page - 1 }))}
                >Previous</button>
                <button
                  className="btn-page"
                  disabled={filters.page >= pagination.totalPages}
                  onClick={() => setFilters(prev => ({ ...prev, page: prev.page + 1 }))}
                >Next</button>
              </div>
            </div>
          )}
        </div>

        {/* Create / Edit Modal */}
        {(showCreateModal || editingTask) && (
          <div className="modal-overlay" onClick={() => { setShowCreateModal(false); setEditingTask(null); }}>
            <div className="modal-content task-modal" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{editingTask ? 'Edit Task' : 'New Task'}</h2>
                <button className="modal-close" onClick={() => { setShowCreateModal(false); setEditingTask(null); }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M18 6L6 18M6 6l12 12"></path>
                  </svg>
                </button>
              </div>
              <div className="modal-body">
                <div className="form-group">
                  <label>Title *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={e => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="What needs to be done?"
                    autoFocus
                  />
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    value={formData.description}
                    onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Add details..."
                    rows={3}
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Priority</label>
                    <select
                      value={formData.priority}
                      onChange={e => setFormData(prev => ({ ...prev, priority: e.target.value }))}
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Assigned To</label>
                    <input
                      type="text"
                      value={formData.assigned_to}
                      onChange={e => setFormData(prev => ({ ...prev, assigned_to: e.target.value }))}
                      placeholder="Name"
                    />
                  </div>
                  <div className="form-group">
                    <label>Due Date</label>
                    <input
                      type="date"
                      value={formData.due_date}
                      onChange={e => setFormData(prev => ({ ...prev, due_date: e.target.value }))}
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn-secondary" onClick={() => { setShowCreateModal(false); setEditingTask(null); }}>Cancel</button>
                <button className="btn-primary" onClick={editingTask ? handleUpdate : handleCreate}>
                  {editingTask ? 'Save Changes' : 'Create Task'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete confirmation */}
        {deleteConfirm && (
          <ConfirmModal
            isOpen={true}
            title="Delete Task"
            message={`Are you sure you want to delete "${deleteConfirm.title}"?`}
            onConfirm={() => handleDelete(deleteConfirm.id)}
            onCancel={() => setDeleteConfirm(null)}
          />
        )}

        {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      </main>
    </div>
  );
};

export default Tasks;
